import React, { useEffect, useState } from 'react'
import CustomRow from '../../reusable-components/CustomRow'
import { Button, Card, Col, Space, Steps, message, theme } from 'antd'
import { CarePlanTemplatesNewRecordTemplate } from '../utils/templates'
import { handleError } from '../../lib/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faChevronLeft, faChevronRight, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { createCarePlanTemplate, updateCarePlanTemplate } from '../utils/utils'
import { useDispatch } from 'react-redux'
import CoreEnums from '../../../core/utils/enums'
import coreReducer from '../../../core/utils/reducer'
import GeneralForm from './WizardSteps/GeneralForm'
import DataTrackerForm from './WizardSteps/DataTrackerForm'
import VisitTrackers from './WizardSteps/VisitTrackers'
import Summary from './WizardSteps/Summary'

// Registry for tab keys by plan type
const getPlanTabKey = (planType, isMonitoring) => {
  const planTabKeys = {
    care: isMonitoring ? CoreEnums.tabKeys.MONITORING_PLANS : CoreEnums.tabKeys.CARE_PLANS,
    treatment: 'TREATMENT_PLANS',
    nutrition: 'NUTRITION_PLANS',
    wellness: 'WELLNESS_PLANS',
    preventive: 'PREVENTIVE_PLANS',
    rehabilitation: 'REHAB_PLANS',
    weight: 'WEIGHT_PLANS'
  }

  return planTabKeys[planType] || CoreEnums.tabKeys.CARE_PLANS
}

const PlanTemplateForm = ({ isNew, recordData, dataPoints, refreshView, isMonitoring, planType, planLabel }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [data, setData] = useState(recordData ? recordData.tracking.filter((i) => i.trackerType === 'data') : [])
  const [visits, setVisits] = useState(recordData ? recordData.tracking.filter((i) => i.trackerType === 'visits') : [])
  const [general, setGeneral] = useState({
    name: recordData ? recordData.name : '',
    description: recordData ? recordData.description : ''
  })
  const dispatch = useDispatch()
  const { token } = theme.useToken()

  // Display the correct plan type in UI
  const displayLabel =
    planLabel || (planType ? planType.charAt(0).toUpperCase() + planType.slice(1) + ' Plan' : 'Care Plan')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleAddTracking = (key, selection) => {
    let tmpData = []
    let tmpVisits = []
    const parsedSelection = JSON.parse(selection)
    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData.push(CarePlanTemplatesNewRecordTemplate(key, parsedSelection.key, parsedSelection.label))
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits.push(CarePlanTemplatesNewRecordTemplate(key, parsedSelection.key, parsedSelection.label))
        setVisits(tmpVisits)
        break
    }
  }

  const handleUpdateTracker = (value, index, property, key) => {
    let tmpData = []
    let tmpVisits = []
    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData[index][property] = value
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits[index][property] = value
        setVisits(tmpVisits)
        break
    }
  }

  const checkGeneralStatus = () => {
    let valid = true
    if (!general.name) {
      valid = false
    }
    return valid
  }

  const handleRemoveTracker = (index, key) => {
    let tmpData = []
    let tmpVisits = []
    switch (key) {
      case 'data':
        tmpData = JSON.parse(JSON.stringify(data))
        tmpData.splice(index, 1)
        setData(tmpData)
        break
      case 'visits':
        tmpVisits = JSON.parse(JSON.stringify(visits))
        tmpVisits.splice(index, 1)
        setVisits(tmpVisits)
        break
    }
  }

  const handleFinalizePlan = async () => {
    let payload = {}

    payload.tracking = [...data, ...visits]
    if (!payload.tracking || payload.tracking?.length === 0) {
      return message.error(`Please provide at least 1 tracker for your ${displayLabel.toLowerCase()}.`)
    }
    payload.name = general.name
    payload.description = general.description
    payload.planType = planType || 'care'

    if (isNew) {
      try {
        await createCarePlanTemplate(payload)
        message.success(`${displayLabel} created successfully.`)
        closeTab()
        refreshView()
      } catch (error) {
        message.error(handleError(error))
      }
    } else {
      await handleUpdatePlan()
    }
  }

  const handleUpdatePlan = async () => {
    let payload = recordData

    payload.tracking = [...data, ...visits]
    if (!payload.tracking || payload.tracking?.length === 0) {
      return message.error(`Please provide at least 1 tracker for your ${displayLabel.toLowerCase()}.`)
    }
    payload.name = general.name
    payload.description = general.description
    payload.planType = planType || 'care'

    try {
      await updateCarePlanTemplate(payload)
      message.success(`${displayLabel} updated successfully.`)
      closeTab()
      refreshView()
    } catch (error) {
      message.error(handleError(error))
    }
  }

  const closeTab = () => {
    const tabKey = getPlanTabKey(planType, isMonitoring)

    dispatch(coreReducer.actions.closeTab(isNew ? `${tabKey}_new` : `${tabKey}_${recordData._id}`))
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Card
          size='small'
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <span>{isNew ? `Create New ${displayLabel}` : `Edit ${displayLabel}`}</span>
              <Button
                danger
                onClick={() => {
                  closeTab()
                }}
              >
                <FontAwesomeIcon icon={faXmarkCircle} />
              </Button>
            </Space>
          }
        >
          <Steps
            current={currentStep}
            onChange={(e) => {
              setCurrentStep(e)
            }}
            items={[
              {
                title: 'General',
                status: checkGeneralStatus() ? 'finish' : 'error'
              },
              {
                title: `${planType === 'health' ? 'Health' : 'Data'} Trackers`,
                status: data.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Visit Trackers',
                status: visits.length > 0 ? 'finish' : 'wait'
              },
              {
                title: 'Summary',
                status: 'wait'
              }
            ]}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card
          size='small'
          extra={
            <Space>
              {currentStep !== 0 ? (
                <Button
                  type='primary'
                  onClick={() => {
                    setCurrentStep(currentStep - 1)
                  }}
                  style={{ marginRight: 8 }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Previous
                </Button>
              ) : undefined}
              {currentStep !== 3 ? (
                <Button
                  disabled={currentStep === 0 && !checkGeneralStatus()}
                  type='primary'
                  onClick={() => {
                    setCurrentStep(currentStep + 1)
                  }}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              ) : (
                <Button
                  type='primary'
                  style={{ background: token.colorSuccess }}
                  onClick={() => {
                    handleFinalizePlan()
                  }}
                >
                  Finalize
                  <FontAwesomeIcon icon={faCheckCircle} />
                </Button>
              )}
            </Space>
          }
        >
          {currentStep === 0 ? (
            <GeneralForm
              isMonitoring={isMonitoring}
              data={general}
              setData={setGeneral}
              planType={planType}
              planLabel={planLabel}
            />
          ) : undefined}
          {currentStep === 1 ? (
            <DataTrackerForm
              handleAddTracking={handleAddTracking}
              handleUpdateTracker={handleUpdateTracker}
              dataPoints={dataPoints}
              data={data}
              handleRemoveTracker={handleRemoveTracker}
              planType={planType}
              planLabel={planLabel}
              trackerType={planType === 'health' ? 'health' : 'data'}
            />
          ) : undefined}
          {currentStep === 2 ? (
            <VisitTrackers
              handleAddTracking={handleAddTracking}
              visits={visits}
              handleUpdateTracker={handleUpdateTracker}
              handleRemoveTracker={handleRemoveTracker}
              planType={planType}
              planLabel={planLabel}
            />
          ) : undefined}
          {currentStep === 3 ? (
            <Summary
              general={general}
              data={data}
              visits={visits}
              isMonitoring={isMonitoring}
              setCurrentStep={setCurrentStep}
              planType={planType}
              planLabel={planLabel}
            />
          ) : undefined}
        </Card>
      </Col>
    </CustomRow>
  )
}

export default PlanTemplateForm
